import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { KeyRound, MonitorSmartphone, ShieldOff } from 'lucide-react';
import { Button } from '../ui/button';
import { confirmDialog } from '../../lib/confirmDialog';

const API = process.env.REACT_APP_BACKEND_URL;
const MODE_LABEL = { cloud: 'Cloud', local_first: 'Local POS' };
const SETUP_LABEL = { unbound: 'Not activated', bound: 'Activated · snapshot pending', ready: 'Active' };

const authHeaders = () => {
  const token = localStorage.getItem('admin_token');
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
};

/** Cloud admin: Local POS status, device list, one-time 6-digit activation code. Raw code lives only in memory. */
export const LocalPosPanel = ({ refreshKey = 0 }) => {
  const [params] = useSearchParams();
  const clubParam = params.get('club');
  const [view, setView] = useState(null);
  const [code, setCode] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const query = clubParam ? `?club_id=${encodeURIComponent(clubParam)}` : '';
      const r = await fetch(`${API}/api/pos/auth/cloud/local-pos${query}`, { headers: authHeaders(), cache: 'no-store' });
      if (r.ok) setView(await r.json());
      else setView({ unavailable: r.status });
    } catch { setView({ unavailable: 0 }); }
  }, [clubParam]);

  useEffect(() => { if (localStorage.getItem('admin_token')) load(); }, [load, refreshKey]);

  const command = async (path, body) => {
    setBusy(true); setError('');
    try {
      const r = await fetch(`${API}/api/pos/auth/cloud/${path}`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(body) });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || 'failed');
      return data;
    } catch (e) {
      setError(typeof e.message === 'string' ? e.message : 'Үйлдэл амжилтгүй. Эрх болон клубын горимыг шалгана уу.');
      return null;
    } finally { setBusy(false); }
  };

  const generate = async () => {
    const data = await command('activation-codes', { club_id: view?.club_id || clubParam || null });
    if (data) { setCode(data); load(); }
  };
  const revokeDevice = async device => {
    if (!await confirmDialog(`Local POS ${device.display_id} эрхийг цуцлах уу? Локал өгөгдөл устахгүй.`, { danger: true })) return;
    if (await command(`devices/${device.device_id}/revoke`, {})) load();
  };

  if (!localStorage.getItem('admin_token') || !view || view.unavailable) return null;
  const localFirst = view.operating_mode === 'local_first';
  return <section data-testid="local-pos-panel" className="space-y-4 border rounded-xl p-4">
    <h2 className="text-lg font-semibold flex items-center gap-2"><MonitorSmartphone size={18} />Local POS · {view.club_name}</h2>
    <p className="text-sm" data-testid="local-pos-mode">Operating mode: <strong>{MODE_LABEL[view.operating_mode]}</strong></p>
    <p className="text-sm" data-testid="local-pos-status">Status: <strong>{SETUP_LABEL[view.local_setup_status]}</strong>
      {view.devices.some(d => d.status === 'revoked') && !view.devices.some(d => d.status === 'active') ? ' · Revoked' : ''}</p>
    {!localFirst && <p data-testid="local-pos-cloud-note" className="text-sm text-amber-600">{view.activation_allowed ? 'Cloud remains active while Local POS is prepared. Final activation requires Super Admin verification.' : 'Prepare Local POS migration in Super Admin before activating a device.'}</p>}
    {localFirst && view.onboarding && <p data-testid="local-pos-onboarding" className="text-sm">Activate Windows POS, then configure tables, staff and inventory locally. No Cloud migration is required.</p>}
    {(localFirst || view.activation_allowed) && <div className="space-y-3">
      <Button data-testid="local-pos-generate-code" disabled={busy} onClick={generate}><KeyRound size={16} className="mr-2" />Generate activation code</Button>
      {code && <div data-testid="local-pos-code-box" className="space-y-1">
        <p data-testid="local-pos-code" className="font-mono text-3xl tracking-widest">{code.code.slice(0, 3)} {code.code.slice(3)}</p>
        <p className="text-sm">Enter this code once on the new RENTOS Local POS. Expires in ~10 minutes.</p>
      </div>}
      {!code && view.active_code && <p data-testid="local-pos-active-code-note" className="text-xs">An activation code is active until {new Date(view.active_code.expires_at).toLocaleTimeString()}. Generate a new one to replace it.</p>}
    </div>}
    <ul data-testid="local-pos-devices" className="space-y-2 text-sm">
      {view.devices.length === 0 && <li data-testid="local-pos-no-devices">No Local POS device yet.</li>}
      {view.devices.map(d => <li key={d.device_id} data-testid={`local-pos-device-${d.display_id}`} className="flex flex-wrap items-center gap-2 justify-between border-t pt-2">
        <span className="font-mono">{d.display_id}</span>
        <span>{d.device_name || d.platform || 'Windows POS'}{d.app_version ? ` · v${d.app_version}` : ''}</span>
        <span data-testid={`local-pos-device-status-${d.display_id}`}>{d.status}{d.last_seen_at ? ` · seen ${new Date(d.last_seen_at).toLocaleString()}` : ''}</span>
        {d.status === 'active' && <Button size="sm" variant="ghost" data-testid={`local-pos-revoke-${d.display_id}`} disabled={busy} onClick={() => revokeDevice(d)}><ShieldOff size={14} className="mr-1" />Revoke</Button>}
      </li>)}
    </ul>
    {error && <p data-testid="local-pos-error" role="alert" className="text-sm text-red-500">{error}</p>}
  </section>;
};
